import React, { useMemo, useCallback, useEffect } from 'react';
import { StacklineTheme } from '@stackline/ui';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import _capitalize from 'lodash/capitalize';
import {
  useAppSelector,
  useMainTimePeriod,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter,
  useHistory
} from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import GrowthMatrixScatterPlot, {
  GrowthMatrixScatterPlotDatum
} from '../GrowthMatrixScatterPlot/GrowthMatrixScatterPlot';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { parseProductGrowthDataForTable } from 'src/components/BeaconV2Components/ExperimentalLayout/productGrowthParser';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { updateUrlQueryParams } from 'src/utils/app';
import { EntityPathname } from 'src/types/application/types';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_GRID_TOP_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { TooltipIcon } from 'src/components/SvgIcons';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

import { styled } from '@mui/styles';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
import { useForecastAdjustmentCount } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastAdjustmentCount';
import { AxiosResponse } from 'axios';
import _get from 'lodash/get';
import { ProductGrowthQuadrant } from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/QuadrantFilterSection';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

import { useLatestForecastModel } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import {
  useBaseMetricRequestBuilder,
  useCreateRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { memoize } from 'lodash';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  BEACON_PRO_TIME_PERIODS,
  useUpdateTimePeriods
} from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';

/**
 * We map the time range options (excluding 'Custom' and 'last week') to a string for use in the product matrix requests which differ slightly from the start and end week options
 */
const useProductGrowthMatrixTimeRange = () => {
  const id = useAppSelector((state) => state.mainTimePeriod.id);
  const timePeriod = useMemo(
    () =>
      ({
        [BEACON_PRO_TIME_PERIODS.LAST_FOUR_WEEKS]: '4',
        [BEACON_PRO_TIME_PERIODS.LAST_THIRTEEN_WEEKS]: '13',
        [BEACON_PRO_TIME_PERIODS.LAST_TWENTY_SIX_WEEKS]: '26',
        [BEACON_PRO_TIME_PERIODS.LAST_FIFTY_TWO_WEEKS]: '52',
        [BEACON_PRO_TIME_PERIODS.YEAR_TO_DATE]: 'YTD'
      }[id] || '4'),
    [id]
  );
  return timePeriod;
};

interface CustomTooltipProps {
  className: string;
  title: React.ReactElement;
  stacklineTheme: StacklineTheme;
  children: React.ReactElement;
  arrow?: boolean;
  placement?: TooltipProps['placement'];
}

/**
 * Max number of points to display in the matrix
 */
export const MATRIX_MAX_POINTS = 5000;

export const CustomTooltip = styled(({ className, title, children, ...props }: CustomTooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} title={title} arrow>
    {children}
  </Tooltip>
))(({ stacklineTheme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: stacklineTheme.colors.secondaryGhost,
    padding: '6px 12px 6px 12px'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: stacklineTheme.colors.secondaryGhost
  }
}));

export const TooltipIconComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function TooltipIconComponent(props, ref) {
    return (
      <div {...props} ref={ref}>
        <TooltipIcon
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        />
      </div>
    );
  }
);

export const TooltipBox = ({ description }: { description: string }) => {
  return (
    <Flex>
      <Text variant="body2">{description}</Text>
    </Flex>
  );
};

// Column Definitions for table
const useFormattedColumnDefinitions = () => {
  const formatMetric = useMetricFormatter(); // Used to format metric values before being displayed in the table
  const {
    defaultQueryParams: { tab: defaultTab, subtab: defaultSubtab }
  } = useAppSelector((state) => state.app);

  const COL_DEFS = useMemo(
    () => [
      // Product Entity
      {
        headerName: 'Products',
        field: 'stacklineSku',
        valueFormatter: (_, row) => {
          return (
            <ProductCell
              key={row.id}
              stacklineSku={row.id}
              title={row.name}
              customLink={updateUrlQueryParams(
                { tab: defaultTab, subtab: defaultSubtab, subcategory: null },
                { path: EntityPathname.Product, routeParam: row.id }
              )}
            />
          );
        }
      },

      // Retail Sales
      {
        field: 'retailSales',
        headerName: 'Retail Sales',
        valueFormatter: (value) => {
          return <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.MONEY)}</div>;
        }
      },
      // Total Traffic
      {
        field: 'beaconTotalTraffic',
        headerName: 'Total Traffic',
        valueFormatter: (value) => {
          return <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.VOLUME)}</div>;
        }
      },
      // Traffic Index
      {
        field: 'trafficIndex',
        headerName: 'Traffic Index',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 1 })}</div>
          );
        }
      },
      // Conversion Rate
      {
        field: 'beaconConversion',
        headerName: 'Conversion Rate',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 1 })}</div>
          );
        }
      },
      // Conversion Index
      {
        field: 'conversionIndex',
        headerName: 'Conversion Index',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 1 })}</div>
          );
        }
      },
      // Adjustment Count
      {
        field: 'adjustmentCount',
        headerName: 'Adjustment Count',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.VOLUME, { showFullValue: true })}</div>
          );
        }
      }
    ],
    [formatMetric, defaultTab, defaultSubtab]
  );
  return COL_DEFS;
};

const getQuadrant = (x: number, y: number): ProductGrowthQuadrant => {
  if (x < 50 && y < 50) {
    return ProductGrowthQuadrant.Rationalize;
  }
  if (x < 50 && y >= 50) {
    return ProductGrowthQuadrant.Upgrade;
  }
  if (x >= 50 && y < 50) {
    return ProductGrowthQuadrant.Invest;
  }
  return ProductGrowthQuadrant.Leverage;
};
const PAGES_PER_CHUNK = 5;
const GRID_ITEMS_PER_PAGE = 20;

/**
 * Fetches the data for the table
 */
const useTableData = (page: number) => {
  const removePriceRangeFilter = useCreateRemoveRetailPriceRangeFilters();
  const filterParamsFromUrl = new URLSearchParams(window.location.search);
  const quadrants = JSON.parse(filterParamsFromUrl.get('filter'))?.quadrants ?? [];
  const { modelVersion, loading: modelVersionLoading } = useLatestForecastModel();
  const timePeriod = useProductGrowthMatrixTimeRange();

  const matrixRequest = useBaseMetricRequestBuilder({
    requestId: 'productGrowthMatrix-table',
    indexName: BEACON_INDEX_NAMES.PRODUCT_GROWTH_MATRIX_WITH_METADATA,
    startWeekId: Number(modelVersion),
    endWeekId: Number(modelVersion),
    fields: []
  });

  matrixRequest
    .addSortFieldWithOnlyFieldName('retailSales', 'desc')
    .setDoAggregation(false)
    .setReturnDocuments(true)
    .setSearchBy('child')
    .addConditionTermFilter('actionType', 'should', quadrants)
    .apply(removePriceRangeFilter())
    .addConditionTermFilter('lookBack', 'must', [timePeriod])
    .setPageNumber(Math.ceil(page / PAGES_PER_CHUNK))
    .setPageSize(GRID_ITEMS_PER_PAGE * PAGES_PER_CHUNK);

  const currentPeriodMatrixRequest = matrixRequest.build();

  // (Matrix) Current Data
  const { data, isLoading } = useGenericAdvancedSearch({
    requestId: `table-productGrowthSubcategoryId-week-${modelVersion}`,
    requestBody: [currentPeriodMatrixRequest],
    shouldPerformFetch: !modelVersionLoading && !!modelVersion,
    queryKeys: [currentPeriodMatrixRequest]
  });

  return { data, isLoading };
};

const ProductGrowthProductTable = () => {
  const COL_DEFS = useFormattedColumnDefinitions();
  const filterParamsFromUrl = new URLSearchParams(window.location.search);
  const stringifiedUrl = filterParamsFromUrl.toString();
  const quadrants = JSON.parse(filterParamsFromUrl.get('filter'))?.quadrants ?? [];

  const [page, setPage] = React.useState(1);
  const [dataByPageNumber, setDataByPageNumber] = React.useState<{ [key: number]: unknown }>({});
  const { data, isLoading: currentDataLoading } = useTableData(page);
  const { modelVersion } = useLatestForecastModel();
  const { id: mainTimePeriodId } = useMainTimePeriod();
  const timePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const formattedTimePeriodShortDisplayName = _capitalize(timePeriodShortDisplayName).replace('week', 'weeks');

  const parseResponse = useCallback(
    (response: AxiosResponse<{ aggregations: { by_stacklineSku: { count: number; fieldId: string }[] } }[]>) => {
      return response.data[0].aggregations.by_stacklineSku.reduce((acc, row) => {
        return {
          ...acc,
          [row.fieldId]: row.count
        };
      }, {} as Record<string, number>);
    },
    []
  );

  const { data: adjustmentCountBySku, isLoading: adjustmentCountBySkuLoading } = useForecastAdjustmentCount({
    buildRequest: (builder) => {
      const stacklineSkus = _get(data, ['data', 0, 'documents'], []).map((row) => row.product.stacklineSku);
      return builder
        .modifyAggregation((aggregation) => aggregation.setGroupByFieldName('stacklineSku'))
        .addConditionTermFilter('stacklineSku', 'should', stacklineSkus)
        .setPageSize(stacklineSkus.length);
    },
    parseResponse,
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !!data && data?.data[0]?.documents?.length > 0 // No need to fetch adjustment counts if no SKUs are returned by the matrix request
    }
  });

  const isLoading = useMemo(
    () => currentDataLoading || adjustmentCountBySkuLoading,
    [currentDataLoading, adjustmentCountBySkuLoading]
  );

  const productRows = useMemo(() => {
    if (!data || !adjustmentCountBySku) {
      return [];
    }
    return parseProductGrowthDataForTable({ data, adjustmentCountBySku });
  }, [data, adjustmentCountBySku]);

  const filterByQuadrant = useCallback(
    (point: Pick<GrowthMatrixScatterPlotDatum, 'x' | 'y'>) => {
      if (quadrants.length === 0) {
        return true;
      }

      return quadrants.includes(getQuadrant(point.x, point.y));
    },
    [quadrants]
  );

  const rowData = useMemo(
    () =>
      productRows
        .map((row) => ({
          ...row,
          x: row.conversionIndex * 100,
          y: row.trafficIndex * 100
        }))
        .filter(filterByQuadrant),
    [productRows, filterByQuadrant]
  );

  useDeepCompareEffect(() => {
    setDataByPageNumber((oldData) => ({
      ...oldData,
      [Math.ceil(page / PAGES_PER_CHUNK)]: rowData
    }));
  }, [rowData, page]);

  React.useEffect(() => {
    setPage(1);
  }, [stringifiedUrl]);

  const completeTableData = React.useMemo(() => {
    try {
      const completeData = Object.entries(dataByPageNumber).reduce((accumulator, [_, collection]) => {
        accumulator = [...accumulator, ...collection];
        return accumulator;
      }, []);
      return completeData;
    } catch (err) {
      console.error('Failed to parse complete dataset in TopicBuilderEditResults', err);
      return [];
    }
  }, [dataByPageNumber]);

  const hidePagination = React.useMemo(
    () =>
      shouldHidePagination({
        page,
        pageSize: GRID_ITEMS_PER_PAGE,
        rowCount: (completeTableData || []).length
      }),
    [page, completeTableData]
  );

  const handlePageNext = useCallback(() => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const startIndex = (adjustedPage - 1) * GRID_ITEMS_PER_PAGE;
    const endIndex = startIndex + GRID_ITEMS_PER_PAGE;

    if (rowData.slice(startIndex, endIndex).length < GRID_ITEMS_PER_PAGE) {
      return;
    }

    setPage(page + 1);
  }, [page, rowData]);

  return (
    <Flex flexDirection="column" gap="lg">
      <EntityTableHeader
        title="Product Details"
        enableSwitchingLayouts
        showDropdown={false}
        iconsList={[IconsList.export]}
        buildExportRequest={({ originalRequest }) => {
          const lookBackMainTimePeriodId = mainTimePeriodId.toUpperCase().replace('W', '');
          const AGGREGATION_LIST = [
            'retailSales',
            'beaconTotalTraffic',
            'trafficIndex',
            'beaconConversion',
            'conversionIndex'
          ];

          const newProductGrowthMetricsAggregations = originalRequest?.[0].aggregations?.[0].aggregationFields.filter(
            (aggregation) => AGGREGATION_LIST.includes(aggregation.aggregateByFieldName)
          );

          const newProductGrowthMetricProductRequest = {
            ...originalRequest[0],
            additionalRequestMetaData: {
              derivedFieldsJson: '[]',
              lookBack: formattedTimePeriodShortDisplayName,
              useNewExports: 'True'
            },
            aggregations: [
              {
                ...originalRequest[0].aggregations[0],
                aggregationFields: newProductGrowthMetricsAggregations,
                conditions: {
                  ...originalRequest[0].aggregations[0].conditions,
                  rangeFilters: [{ fieldName: 'weekId', minValue: modelVersion, maxValue: modelVersion }]
                }
              }
            ],

            conditions: {
              ...originalRequest[0].conditions,
              termFilters: [
                ...originalRequest[0].conditions.termFilters,
                { fieldName: 'actionType', condition: 'should', values: quadrants },

                { fieldName: 'lookBack', condition: 'must', values: [lookBackMainTimePeriodId] }
              ],
              rangeFilters: [
                ...originalRequest[0].conditions.rangeFilters,
                { fieldName: 'weekId', minValue: modelVersion, maxValue: modelVersion }
              ]
            }
          };

          const newBeaconAdjustmentRequest = {
            ...originalRequest[1],
            additionalRequestMetaData: {
              derivedFieldsJson: '[]',
              lookBack: formattedTimePeriodShortDisplayName,
              useNewExports: 'True'
            },
            aggregations: [
              {
                ...originalRequest[1].aggregations[0],
                conditions: { ...originalRequest[1].aggregations[0].conditions, rangeFilters: [] }
              }
            ],

            conditions: {
              ...originalRequest[1].conditions,
              termFilters: [
                ...originalRequest[1].conditions.termFilters,
                { fieldName: 'status', condition: 'must', values: ['Published'] }
              ],
              rangeFilters: []
            },
            exportResults: true
          };

          const newRequest = [newProductGrowthMetricProductRequest, newBeaconAdjustmentRequest];

          return newRequest;
        }}
      />
      <Flex flexDirection="column" alignItems="flex-end">
        <EntityTable
          page={page}
          showPagination={false}
          isLoading={isLoading}
          rowData={completeTableData}
          columnDefs={COL_DEFS}
          shouldModifyColumnDefs={false}
        />
        {hidePagination && (
          <Pagination
            currentPage={page}
            shouldUseInfinitePagination
            onClickNext={handlePageNext}
            onClickPrevious={() => setPage((pg) => Math.max(pg - 1, 1))}
          />
        )}
      </Flex>
      <EntityTableHeader />
    </Flex>
  );
};

const ProductGrowthPage = () => {
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const formatMetric = useMetricFormatter();
  const { modelVersion, loading: modelVersionLoading } = useLatestForecastModel();
  const filterParamsFromUrl = new URLSearchParams(window.location.search);
  const removePriceRangeFilter = useCreateRemoveRetailPriceRangeFilters();
  const quadrants = JSON.parse(filterParamsFromUrl.get('filter'))?.quadrants ?? [];
  const timePeriod = useProductGrowthMatrixTimeRange();

  // Handle navigation with custom date - change to YTD since custom date is not allowed
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const history = useHistory();
  const updateTimePeriods = useUpdateTimePeriods({ history });

  useEffect(() => {
    if (mainTimePeriod.id === BEACON_PRO_TIME_PERIODS.CUSTOM) {
      const ytdTimePeriod = mainTimePeriod.availableMainTimePeriods.find(
        (period) => period.id === BEACON_PRO_TIME_PERIODS.YEAR_TO_DATE
      );

      if (ytdTimePeriod) {
        updateTimePeriods(ytdTimePeriod);
      }
    }
  }, [mainTimePeriod.id, mainTimePeriod.availableMainTimePeriods, updateTimePeriods]);

  const matrixRequest = useBaseMetricRequestBuilder({
    requestId: 'productGrowthMatrix',
    indexName: BEACON_INDEX_NAMES.PRODUCT_GROWTH_MATRIX,
    startWeekId: Number(modelVersion),
    endWeekId: Number(modelVersion),
    fields: []
  });

  matrixRequest
    .addSortFieldWithOnlyFieldName('retailSales', 'desc')
    .setDoAggregation(false)
    .setReturnDocuments(true)
    .setSearchBy('child')
    .apply(removePriceRangeFilter())
    .addConditionTermFilter('lookBack', 'must', [timePeriod])
    .setPageSize(MATRIX_MAX_POINTS);

  const currentPeriodMatrixRequest = matrixRequest.build();

  // (Matrix) Current Data
  const { data, isLoading: currentDataLoading } = useGenericAdvancedSearch({
    requestId: `productGrowthSubcategoryId-week-${modelVersion}`,
    requestBody: [currentPeriodMatrixRequest],
    shouldPerformFetch: !modelVersionLoading && !!modelVersion,
    queryKeys: [currentPeriodMatrixRequest]
  });

  const isLoading = useMemo(() => currentDataLoading || modelVersionLoading, [currentDataLoading, modelVersionLoading]);

  // Pre-compute the icons for each point to avoid recalculating them on each render
  const getPointIcons = memoize(
    (x: number, y: number): { activeIcon: string; disabledIcon: string } => {
      const svgPath =
        x < 50 && y < 50 ? 'orangeDot' : x < 50 && y >= 50 ? 'greenDot' : x > 50 && y < 50 ? 'tealDot' : 'blueDot';

      // capitalize just the first letter, e.g. mutedBlueDot
      const disabledSvgPath = `muted${svgPath.charAt(0).toUpperCase()}${svgPath.slice(1)}`;

      return { activeIcon: `url(/svg/${svgPath}.svg)`, disabledIcon: `url(/svg/${disabledSvgPath}.svg)` };
    },
    (x, y) => `${x}-${y}`
  );

  const matrixData: GrowthMatrixScatterPlotDatum[] = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.data[0].documents.map((row) => {
      // Detailslink should redirect to keymetrics page instead of growthmatrix page.
      const modifiedAdditionParamsForCard = additionalParams.replace('growthmatrix', 'keymetrics');

      const { activeIcon, disabledIcon } = getPointIcons(row.conversionIndex * 100, row.trafficIndex * 100);
      return {
        icon: activeIcon,
        disabledIcon,
        retailSales: row.retailSales,
        x: row.conversionIndex * 100,
        y: row.trafficIndex * 100,
        conversionIndex: formatMetric(row.conversionIndex, METRICTYPE.PERCENT, { showFullValue: false }),
        trafficIndex: formatMetric(row.trafficIndex, METRICTYPE.PERCENT, { showFullValue: false }),
        imageUri: `https://s3-us-west-2.amazonaws.com/stackline-product-images/${row.stacklineSku}.jpg`,
        statusCode: row.actionType,
        statusColor: {
          Upgrade: 'success',
          Rationalize: 'accentTangerine',
          Leverage: 'info',
          Invest: 'accentTeal'
        }[row.actionType],
        detailsLink: `/product/${row.stacklineSku}${searchParams}${modifiedAdditionParamsForCard}`,
        stacklineSku: row.stacklineSku
      } as GrowthMatrixScatterPlotDatum;
    });
  }, [data, formatMetric, additionalParams, searchParams, getPointIcons]);

  const filterByQuadrant = useCallback(
    (point: Pick<GrowthMatrixScatterPlotDatum, 'x' | 'y'>) => {
      if (quadrants.length === 0) {
        return true;
      }

      return quadrants.includes(getQuadrant(point.x, point.y));
    },
    [quadrants]
  );

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_GRID_TOP_SPACING}>
        <Flex flexDirection="column" gap="lg">
          <CommonSummaryInfoSubtitle
            title={
              <Flex alignItems="center">
                <Text variant="h4">Product Growth Matrix</Text>
              </Flex>
            }
          />
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.BEACON_PRODUCT_GROWTH_MATRIX.name}
            chartName={CHART_NAMES.BEACON_PRODUCT_GROWTH_MATRIX_SCATTER}
          >
            <BeaconChartWithLegend
              title=""
              showTitleSummary={false}
              loading={isLoading}
              loadingComponent={<SlSkeleton variant="rounded" width="100%" height={500} />}
            >
              <GrowthMatrixScatterPlot
                xAxisLabel="Conversion Index"
                yAxisLabel="Traffic Index"
                data={matrixData.filter(filterByQuadrant)}
              />
            </BeaconChartWithLegend>
          </ChartContextProvider>
        </Flex>
        <ProductGrowthProductTable />
      </Flex>
    </BasePageContainer>
  );
};

export default ProductGrowthPage;
